import React, { useState } from 'react';
import { Briefcase, Star, MapPin, Clock, X, Upload, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { careersData } from '../careersData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    coverLetter: ''
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setSubmitStatus(null);
  };

  const handleCloseModal = () => {
    setSelectedJob(null);
    setFormData({ name: '', email: '', phone: '', linkedin: '', coverLetter: '' });
    setResume(null);
    setSubmitStatus(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5000000) { // 5MB limit
      setResume(file);
    } else {
      alert('File size must be less than 5MB');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // For now, send without file - would need multipart/form-data for real file upload
      const response = await axios.post(`${BACKEND_URL}/api/job-application`, {
        ...formData,
        jobTitle: selectedJob.title,
        jobDepartment: selectedJob.department,
        resumeFileName: resume ? resume.name : 'No resume attached'
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          handleCloseModal();
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-black via-gray-900 to-black py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
            Careers at <span className="text-yellow-400">bHyyve</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join our mission to transform how Ontarians access products and services
          </p>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-black mb-8 text-center">Why Work at bHyyve?</h2>
          <p className="text-gray-700 text-lg leading-relaxed text-center mb-12">
            We're building the future of product and service delivery. Join a team of passionate, talented individuals 
            who are making a real impact on people's lives every day.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {careersData.benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
                <p className="text-gray-700 font-semibold">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Reviews */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-3 mb-12">
            <Star className="text-yellow-400 fill-yellow-400" size={36} />
            <h2 className="text-4xl font-bold text-black text-center">What Our Team Says</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {careersData.companyReviews.map((review) => (
              <div
                key={review.id}
                className="bg-white border-2 border-gray-200 rounded-2xl p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-1">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                    ))}
                  </div>
                  <span className="text-gray-500 text-sm">{review.date}</span>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "{review.text}"
                </p>
                <div className="border-t-2 border-gray-100 pt-4">
                  <p className="font-bold text-black">{review.author}</p>
                  <p className="text-gray-500 text-sm">{review.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-3 mb-12">
            <Briefcase className="text-yellow-400" size={36} />
            <h2 className="text-4xl font-bold text-black text-center">Open Positions</h2>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {careersData.jobs.map((job) => (
              <div
                key={job.id}
                className="bg-white border-2 border-gray-200 hover:border-yellow-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-gray-600 mb-4">
                      <span className="flex items-center gap-2">
                        <Briefcase size={18} />
                        {job.department}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin size={18} />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-2">
                        <Clock size={18} />
                        {job.type}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-4">{job.description}</p>
                    <div className="mb-4">
                      <p className="font-semibold text-black mb-2">Requirements:</p>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        {job.requirements.map((req, index) => (
                          <li key={index}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleApplyClick(job)}
                    data-testid={`apply-btn-${job.id}`}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-8 py-3 rounded-xl whitespace-nowrap"
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 relative my-8">
            <button
              onClick={handleCloseModal}
              data-testid="close-modal-btn"
              className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
            >
              <X size={24} />
            </button>

            <h2 className="text-3xl font-bold text-black mb-2">Apply for {selectedJob.title}</h2>
            <p className="text-gray-600 mb-6">{selectedJob.department} • {selectedJob.location}</p>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                <div>
                  <p className="text-green-800 font-semibold">Application Submitted!</p>
                  <p className="text-green-700 text-sm">We'll review your application and get back to you soon.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                <div>
                  <p className="text-red-800 font-semibold">Submission Failed</p>
                  <p className="text-red-700 text-sm">Please try again or email jobs@bhyyve.com</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-black mb-2">Full Name *</label>
                <Input
                  name="name"
                  data-testid="applicant-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  data-testid="applicant-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSubmitting || submitStatus === 'success'}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">LinkedIn Profile</label>
                <Input
                  type="url"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  disabled={isSubmitting || submitStatus === 'success'}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Cover Letter *</label>
                <Textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                  rows={6}
                  placeholder="Tell us why you're a great fit for this role..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Resume * (PDF, Max 5MB)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-yellow-400 transition-colors">
                  <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    disabled={isSubmitting || submitStatus === 'success'}
                    className="hidden"
                    id="resume-upload"
                    required
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer text-gray-600 hover:text-black">
                    {resume ? (
                      <span className="text-green-600 font-semibold">{resume.name}</span>
                    ) : (
                      <span>Click to upload or drag and drop</span>
                    )}
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-application-btn"
                disabled={isSubmitting || submitStatus === 'success'}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                    Submitting...
                  </>
                ) : submitStatus === 'success' ? (
                  'Application Submitted!'
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    Submit Application
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;
